import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

/** Only the summary fields we actually show in the preview row. */
interface ScenarioSummary {
  readonly tenYearNetBenefits?: number;
  readonly totalIncentive?: number;
  readonly paybackPeriodYears?: number | null;
}

interface ScenarioRow {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly status: 'draft' | 'ready';
  readonly results: {
    readonly summary?: ScenarioSummary;
  } | null;
}

export interface BaseSummaryForScenarios {
  readonly tenYearNetBenefits: number | null;
  readonly totalIncentive: number | null;
  readonly paybackPeriodYears: number | null;
}

interface Props {
  readonly analysisId: string;
  /** Base-analysis summary metrics used to compute per-scenario deltas. */
  readonly baseSummary: BaseSummaryForScenarios;
}

const MAX_PREVIEW = 3;

/**
 * Inline scenarios preview for the analysis result page.
 *
 * Each scenario row shows three compact at-a-glance metrics with a small
 * colored delta vs. the base analysis:
 *   - 10-year net benefit
 *   - Payback period
 *   - Total incentive cost
 *
 * The earlier version only surfaced net-benefit delta, which frequently
 * rendered as "$0 (0.0%)" for scenarios that re-shaped the incentive
 * package without changing the bottom line — giving the misleading
 * impression that the scenario had no effect. Surfacing incentive cost
 * and payback exposes *how* the scenario differs even when the long-run
 * fiscal math lands in the same place.
 *
 * Full side-by-side diff still lives on /analyses/:id/compare.
 */
export function ScenariosPreviewPanel({ analysisId, baseSummary }: Props) {
  const getToken = useSessionTokenGetter();
  const [scenarios, setScenarios] = useState<ScenarioRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${analysisId}/scenarios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = (await res.json()) as { scenarios: ScenarioRow[] };
        if (!cancelled) setScenarios(data.scenarios);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [analysisId, getToken]);

  if (error) {
    return (
      <section className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Could not load scenarios: {error}
      </section>
    );
  }

  if (!scenarios) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
        Loading scenarios…
      </section>
    );
  }

  const readyScenarios = scenarios.filter((s) => s.status === 'ready' && s.results?.summary);
  const previewScenarios = scenarios.slice(0, MAX_PREVIEW);
  const hiddenCount = Math.max(0, scenarios.length - MAX_PREVIEW);

  return (
    <section
      aria-label="Scenarios preview"
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">Scenarios</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {scenarios.length === 0
              ? 'Test alternative incentive packages or assumption sets.'
              : `${scenarios.length} scenario${scenarios.length === 1 ? '' : 's'}, ${
                  readyScenarios.length
                } run`}
          </p>
        </div>
        <div className="flex shrink-0 gap-3 text-xs font-medium">
          <Link
            to={`/analyses/${analysisId}/scenarios`}
            className="text-slate-600 hover:text-slate-900 hover:underline"
          >
            Manage →
          </Link>
          {readyScenarios.length >= 1 && (
            <Link
              to={`/analyses/${analysisId}/compare`}
              className="text-indigo-600 hover:text-indigo-800 hover:underline"
            >
              Compare all →
            </Link>
          )}
        </div>
      </div>

      {scenarios.length === 0 ? (
        <div className="mt-4 rounded-md border border-dashed border-slate-300 p-4 text-center">
          <p className="text-sm text-slate-600">
            No scenarios yet. Create one to test a different incentive package or assumption.
          </p>
          <Link
            to={`/analyses/${analysisId}/scenarios`}
            className="mt-2 inline-block text-sm font-medium text-indigo-600 hover:underline"
          >
            Create first scenario →
          </Link>
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
          {previewScenarios.map((s) => (
            <ScenarioPreviewRow
              key={s.id}
              scenario={s}
              base={baseSummary}
              analysisId={analysisId}
            />
          ))}
          {hiddenCount > 0 && (
            <li className="pt-2.5 text-center">
              <Link
                to={`/analyses/${analysisId}/scenarios`}
                className="text-xs font-medium text-slate-600 hover:text-slate-900 hover:underline"
              >
                + {hiddenCount} more
              </Link>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

function ScenarioPreviewRow({
  scenario,
  base,
  analysisId,
}: {
  scenario: ScenarioRow;
  base: BaseSummaryForScenarios;
  analysisId: string;
}) {
  const summary = scenario.results?.summary;
  const hasResults =
    scenario.status === 'ready' &&
    summary !== undefined &&
    summary.tenYearNetBenefits !== undefined;

  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3">
      <div className="flex min-w-0 flex-1 items-baseline gap-2">
        <Link
          to={`/analyses/${analysisId}/scenarios/${scenario.id}`}
          className="truncate text-sm font-medium text-slate-900 hover:text-indigo-700 hover:underline"
        >
          {scenario.name}
        </Link>
        {scenario.status === 'draft' && (
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600">
            Draft
          </span>
        )}
      </div>
      {hasResults ? (
        <div className="flex shrink-0 flex-wrap items-baseline gap-x-4 gap-y-1 tabular-nums">
          <Metric
            label="Net 10-yr"
            value={formatCompactCurrency(summary?.tenYearNetBenefits)}
            delta={numericDelta(summary?.tenYearNetBenefits, base.tenYearNetBenefits)}
            format="currency-pct"
          />
          <Metric
            label="Payback"
            value={formatYears(summary?.paybackPeriodYears)}
            delta={numericDelta(summary?.paybackPeriodYears, base.paybackPeriodYears)}
            format="years"
            /** lower payback = better, so color flips */
            lowerIsBetter
          />
          <Metric
            label="Incentive"
            value={formatCompactCurrency(summary?.totalIncentive)}
            delta={numericDelta(summary?.totalIncentive, base.totalIncentive)}
            format="currency-pct"
            /** lower incentive cost = better */
            lowerIsBetter
          />
        </div>
      ) : (
        <span className="shrink-0 text-sm text-slate-400">Not run</span>
      )}
    </li>
  );
}

/**
 * One label/value pair with an optional colored delta pill. Self-contained so
 * the row layout stays flat — easier to flex-wrap on narrow viewports.
 */
function Metric({
  label,
  value,
  delta,
  format,
  lowerIsBetter = false,
}: {
  label: string;
  value: string;
  delta: number | null;
  format: 'currency-pct' | 'years';
  lowerIsBetter?: boolean;
}) {
  let deltaNode: React.ReactNode = null;
  if (delta !== null) {
    const isFlat = Math.abs(delta) < (format === 'years' ? 0.05 : 1);
    const isGood = lowerIsBetter ? delta < 0 : delta > 0;
    const colorClass = isFlat ? 'text-slate-400' : isGood ? 'text-emerald-700' : 'text-red-700';
    const prefix = delta > 0 ? '+' : '';
    const text =
      format === 'years'
        ? `${prefix}${delta.toFixed(1)}y`
        : `${prefix}${formatCompactCurrency(delta)}`;
    deltaNode = <span className={`ml-1 text-[11px] ${colorClass}`}>{text}</span>;
  }
  return (
    <div className="flex flex-col text-right leading-tight">
      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <span className="text-sm font-medium text-slate-800">
        {value}
        {deltaNode}
      </span>
    </div>
  );
}

/** Returns scenarioValue - baseValue, or null if either side is missing. */
function numericDelta(
  scenarioValue: number | null | undefined,
  baseValue: number | null | undefined,
): number | null {
  if (
    scenarioValue === null ||
    scenarioValue === undefined ||
    baseValue === null ||
    baseValue === undefined
  ) {
    return null;
  }
  return scenarioValue - baseValue;
}

function formatCompactCurrency(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

function formatYears(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${n.toFixed(1)} yrs`;
}
